import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchPredictions } from "@/lib/edgeFunctions";

// Types for predictions data
export interface Prediction {
  id: string;
  fixture_id: number;
  game_id: string | null;
  winner_id: number | null;
  winner_name: string | null;
  winner_comment: string | null;
  win_or_draw: boolean | null;
  under_over: string | null;
  goals_home: string | null;
  goals_away: string | null;
  advice: string | null;
  percent_home: string | null;
  percent_draw: string | null;
  percent_away: string | null;
  created_at: string;
  updated_at: string;
}

export interface PredictionTeamStats {
  id: string;
  prediction_id: string;
  team_id: string | null;
  team_api_id: number | null;
  is_home: boolean;
  league_form: string | null;
  league_fixtures_played_home: number | null;
  league_fixtures_played_away: number | null;
  league_fixtures_wins_home: number | null;
  league_fixtures_wins_away: number | null;
  league_fixtures_draws_home: number | null;
  league_fixtures_draws_away: number | null;
  league_fixtures_loses_home: number | null;
  league_fixtures_loses_away: number | null;
  league_goals_for_total_home: number | null;
  league_goals_for_total_away: number | null;
  league_goals_against_total_home: number | null;
  league_goals_against_total_away: number | null;
  last_5_form: string | null;
  last_5_att: string | null;
  last_5_def: string | null;
  last_5_goals_for_total: number | null;
  last_5_goals_against_total: number | null;
  last_5_goals_for_average: string | null;
  last_5_goals_against_average: string | null;
  clean_sheet_home: number | null;
  clean_sheet_away: number | null;
  clean_sheet_total: number | null;
  failed_to_score_home: number | null;
  failed_to_score_away: number | null;
  penalty_scored_total: number | null;
  penalty_scored_percentage: string | null;
  penalty_missed_total: number | null;
  penalty_missed_percentage: string | null;
  biggest_streak_wins: number | null;
  biggest_streak_draws: number | null;
  biggest_streak_loses: number | null;
  goals_for_by_minute: any | null;
  goals_against_by_minute: any | null;
  fallback_source: string | null;
  raw_json: any | null;
}

export interface PredictionComparison {
  id: string;
  prediction_id: string;
  form_home: string | null;
  form_away: string | null;
  att_home: string | null;
  att_away: string | null;
  def_home: string | null;
  def_away: string | null;
  poisson_distribution_home: any | null;
  poisson_distribution_away: any | null;
  h2h_home: string | null;
  h2h_away: string | null;
  goals_home: string | null;
  goals_away: string | null;
  total_home: string | null;
  total_away: string | null;
}

export interface PredictionH2H {
  id: string;
  prediction_id: string;
  fixture_id: number;
  match_date: string | null;
  home_team_id: number | null;
  home_team_name: string | null;
  away_team_id: number | null;
  away_team_name: string | null;
  home_goals: number | null;
  away_goals: number | null;
}

export interface PredictionsData {
  prediction: Prediction | null;
  teamStats: {
    home: PredictionTeamStats | null;
    away: PredictionTeamStats | null;
  };
  comparison: PredictionComparison | null;
  h2h: PredictionH2H[];
}

export const usePredictions = (fixtureId: number | null) => {
  return useQuery({
    queryKey: ['predictions', fixtureId],
    queryFn: async (): Promise<PredictionsData | null> => {
      if (!fixtureId) return null;

      // Fetch main prediction
      const { data: prediction, error: predError } = await supabase
        .from('predictions')
        .select('*')
        .eq('fixture_id', fixtureId)
        .maybeSingle();

      if (predError) {
        console.error('Error fetching prediction:', predError);
        throw predError;
      }

      if (!prediction) {
        return null;
      }

      // Fetch team stats
      const { data: teamStats, error: statsError } = await supabase
        .from('prediction_team_stats')
        .select('*')
        .eq('prediction_id', prediction.id);

      if (statsError) {
        console.error('Error fetching team stats:', statsError);
      }

      // Fetch comparison
      const { data: comparison, error: compError } = await supabase
        .from('prediction_comparison')
        .select('*')
        .eq('prediction_id', prediction.id)
        .maybeSingle();

      if (compError) {
        console.error('Error fetching comparison:', compError);
      }

      // Fetch H2H
      const { data: h2h, error: h2hError } = await supabase
        .from('prediction_h2h')
        .select('*')
        .eq('prediction_id', prediction.id)
        .order('match_date', { ascending: false });

      if (h2hError) {
        console.error('Error fetching H2H:', h2hError);
      }

      return {
        prediction: prediction as Prediction,
        teamStats: {
          home: (teamStats?.find(s => s.is_home) || null) as PredictionTeamStats | null,
          away: (teamStats?.find(s => !s.is_home) || null) as PredictionTeamStats | null,
        },
        comparison: comparison as PredictionComparison | null,
        h2h: (h2h || []) as PredictionH2H[],
      };
    },
    enabled: !!fixtureId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useFetchPredictions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fixtureId: number) => {
      return await fetchPredictions(fixtureId);
    },
    onSuccess: (_, fixtureId) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['predictions', fixtureId] });
    },
  });
};
